import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Theme, alpha, styled } from "@mui/material/styles";
import { ElementType } from "react";
import { Link as RouterLink } from "react-router-dom";

import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";
import { colors } from "@/theme";

import { accentColor, borderColor, headerBg, isDarkTheme, surfaceBg } from "../../utils/matchOdds";

/* Pieces every sportsbook screen uses: cards, tabs, badges, the live dot, avatars. */

export const SurfaceCard = styled(Box)(({ theme }) => ({
  scrollMarginTop: 12,
  borderRadius: "10px",
  overflow: "hidden",
  border: `1px solid ${borderColor(theme)}`,
  background: surfaceBg(theme),
}));

/**
 * A section inside a card: a league inside its sport. A rule between
 * neighbours instead of a card of its own, so a sport's leagues read as one
 * list and not as boxes in a box.
 */
export const FlatSection = styled(Box)(({ theme }) => ({
  scrollMarginTop: 12,
  "& + &": { borderTop: `1px solid ${borderColor(theme)}` },
}));

export const AccentCountBadge = styled(Box)(({ theme }) => ({
  minWidth: 20,
  paddingLeft: 6,
  paddingRight: 6,
  borderRadius: "10px",
  fontSize: 11,
  lineHeight: "20px",
  textAlign: "center" as const,
  fontVariantNumeric: "tabular-nums",
  background: surfaceBg(theme),
  color: accentColor(theme),
  border: `1px solid ${accentColor(theme)}`,
}));

/** The keyboard's ring on a header that folds: the sky blue on the night, the deep ink on paper. */
export const headerFocusRing = (theme: Theme) => ({
  "&:focus-visible": {
    outline: `2px solid ${isDarkTheme(theme) ? MASCOT_PALETTE.sky : accentColor(theme)}`,
    outlineOffset: -2,
  },
});

/** The side that is serving (tennis, volleyball, table tennis): a sky dot after its name. */
export const ServeDot = styled("span")(({ theme }) => ({
  flexShrink: 0,
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: isDarkTheme(theme) ? MASCOT_PALETTE.sky : accentColor(theme),
  boxShadow: `0 0 0 2px ${alpha(colors.prime, 0.25)}`,
}));

/** When a match that has not started starts: "Today 19:30", with the day in the page ink. */
export const Kickoff = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  lineHeight: "18px",
  color: theme.palette.text.secondary,
  fontVariantNumeric: "tabular-nums",
  "& b": { fontWeight: 600, color: theme.palette.text.primary },
}));

export const SportPanel = styled(Box)(({ theme }) => ({
  marginTop: 16,
  padding: 0,
  borderRadius: "8px",
  background: surfaceBg(theme),
  "@media (min-width: 900px)": {
    padding: 12,
  },
}));

export const tabStyles = (theme: Theme, active: boolean) => {
  const accent = accentColor(theme);
  const bg = headerBg(theme);
  const overlay = isDarkTheme(theme) ? colors.gradientSidebarDark2x : colors.gradientSidebarLight;

  return {
    gap: 8,
    paddingLeft: 12,
    paddingRight: 6,
    paddingTop: 6,
    paddingBottom: 6,
    borderRadius: "20px",
    background: active ? `${overlay}, ${bg}` : bg,
    border: `1px solid ${active ? accent : borderColor(theme)}`,
    color: active ? accent : theme.palette.text.secondary,
    transition: "color .2s ease, border-color .2s ease",
    "@media (hover: hover)": {
      "&:hover": { borderColor: accent, color: accent },
    },
    "&:focus-visible": {
      outline: `2px solid ${isDarkTheme(theme) ? MASCOT_PALETTE.sky : accent}`,
      outlineOffset: 2,
    },
  };
};

export const TabButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ theme, active }) => tabStyles(theme, active));

/** A tab that goes somewhere: TabButton's look on a router link, so a middle click opens a new tab. */
export const TabLink = styled(RouterLink, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ theme, active }) => ({
  display: "inline-flex",
  alignItems: "center",
  textDecoration: "none",
  ...tabStyles(theme, active),
}));

const pulse = {
  "0%": {
    transform: "scale(0.85)",
    boxShadow: `0 0 0 0 ${alpha(colors.prime, 0.7)}`,
  },
  "70%": {
    transform: "scale(1)",
    boxShadow: `0 0 0 8px ${alpha(colors.prime, 0)}`,
  },
  "100%": {
    transform: "scale(0.85)",
    boxShadow: `0 0 0 0 ${alpha(colors.prime, 0)}`,
  },
};

/** The pulse; `component="span"` where it sits in a paragraph. */
export const LiveDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone?: "live" | "alert"; component?: ElementType }>(({ theme, tone }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: tone === "alert" ? theme.palette.error.main : colors.prime,
  animation: tone === "live" ? "livePulse 1.4s ease-out infinite" : undefined,
  "@keyframes livePulse": pulse,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
}));

export const StatusBadge = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: "live" | "muted" | "alert" }>(({ theme, tone }) => {
  const live = isDarkTheme(theme) ? MASCOT_PALETTE.sky : accentColor(theme);
  const ink =
    tone === "live"
      ? live
      : tone === "alert"
        ? theme.palette.error.main
        : theme.palette.text.secondary;

  return {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px",
    borderRadius: "12px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    width: "fit-content",
    maxWidth: "100%",
    color: ink,
    border: `1px solid ${tone === "muted" ? borderColor(theme) : alpha(ink, 0.6)}`,
    background: tone === "muted" ? headerBg(theme) : alpha(ink, 0.08),
  };
});

export const TeamAvatarRoot = styled(Avatar)(({ theme }) => ({
  width: 56,
  height: 56,
  flexShrink: 0,
  fontSize: 20,
  fontWeight: 700,
  color: accentColor(theme),
  background: headerBg(theme),
  border: `1px solid ${accentColor(theme)}`,
}));

/** The logo over the placeholder; it shows only once it has loaded. */
export const TeamAvatarLogo = styled("img", {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ visible }) => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  opacity: visible ? 1 : 0,
  transition: "opacity .2s ease",
}));
